import { useEffect, useId, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthProvider'
import { cn } from '@/shared/ui/primitives'
import { Icon, type IconName } from './icons'
import { TONE_CLASS, visibleModules, type ModuleDef } from './modules'

const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'

/**
 * The module switcher: the top bar's answer to "which application am I in".
 *
 * The button always names where you are -- "Beranda" on the hub, the module's
 * short name inside a module -- and sits in the same place on every screen, so
 * moving between modules never depends on first finding your way back home.
 * The sidebar underneath is left to answer the other question, "which page".
 */
export function ModuleSwitcher({ current }: { current?: ModuleDef }) {
  const { can } = useAuth()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()

  const modules = visibleModules(can)

  // Arriving somewhere closes it, however the navigation happened.
  useEffect(() => setOpen(false), [location.pathname])

  useEffect(() => {
    if (!open) return
    // Focus lands on where you are, so arrow-free Tab order starts from a
    // known place and Enter on it is a harmless no-op.
    const panel = panelRef.current
    ;(panel?.querySelector<HTMLElement>('[aria-current="true"]') ?? panel?.querySelector<HTMLElement>('a'))?.focus()

    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        title="Pindah modul"
        className={cn(
          'flex h-10 items-center gap-2 rounded-md border border-line bg-card px-2.5 font-medium text-gray-900 transition-colors',
          'hover:border-primary aria-expanded:border-primary',
          FOCUS_RING,
        )}
      >
        <Icon name="apps" className="size-4.5 shrink-0 text-primary" />
        <span className="hidden sm:inline">{current?.short ?? 'Beranda'}</span>
        <span className="sr-only sm:hidden">Pindah modul, sekarang di {current?.short ?? 'Beranda'}</span>
        <Icon
          name="chevronDown"
          className={cn('size-4 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Pindah modul"
          className={cn(
            'animate-rise absolute top-[calc(100%+0.5rem)] left-0 z-40 w-[26rem] rounded-lg border border-line bg-card p-2 shadow-card-lg',
            // On a phone the panel spans the screen: anchored to a button
            // near the left edge it would run off the right one.
            'max-sm:fixed max-sm:inset-x-4 max-sm:top-17 max-sm:w-auto',
          )}
        >
          <div className="flex items-baseline justify-between gap-3 px-2.5 pt-1.5 pb-2.5">
            <span className="font-semibold text-gray-900">Pindah modul</span>
            <span className="text-xs text-gray-500">Anda di {current?.short ?? 'Beranda'}</span>
          </div>

          <Entry
            to="/"
            icon="home"
            iconClass="bg-gray-200 text-gray-700"
            name="Beranda"
            sub="Ringkasan kerja hari ini"
            current={!current}
          />

          {modules.length > 0 ? (
            <div className="mt-1.5 grid gap-1 border-t border-line pt-1.5 sm:grid-cols-2">
              {modules.map((module) => {
                const isCurrent = module.id === current?.id
                const sub = isCurrent ? 'Sedang dibuka' : module.kind === 'external' ? module.hint : module.tagline
                return module.kind === 'internal' ? (
                  <Entry
                    key={module.id}
                    to={module.basePath}
                    icon={module.icon}
                    iconClass={TONE_CLASS[module.tone].soft}
                    name={module.short}
                    sub={sub}
                    current={isCurrent}
                  />
                ) : (
                  <Entry
                    key={module.id}
                    href={module.href()}
                    icon={module.icon}
                    iconClass={TONE_CLASS[module.tone].soft}
                    name={module.short}
                    sub={sub}
                    external
                  />
                )
              })}
            </div>
          ) : (
            <p className="mt-1.5 border-t border-line px-2.5 pt-3 pb-1 text-gray-500">
              Belum ada modul untuk peran Anda.
            </p>
          )}

          <p className="mt-1.5 flex items-center gap-1.5 border-t border-line px-2.5 pt-2.5 pb-1 text-xs text-gray-500">
            <kbd className="rounded border border-gray-300 bg-card px-1.5 py-0.5 font-sans">Ctrl K</kbd>
            untuk lompat langsung ke halaman mana pun
          </p>
        </div>
      )}
    </div>
  )
}

function Entry({
  to,
  href,
  icon,
  iconClass,
  name,
  sub,
  current = false,
  external = false,
}: {
  to?: string
  href?: string
  icon: IconName
  iconClass: string
  name: string
  sub: string
  current?: boolean
  external?: boolean
}) {
  const className = cn(
    'grid grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-x-3 rounded-md border px-2.5 py-2 text-left transition-colors',
    current
      ? 'border-primary/30 bg-primary-light'
      : 'border-transparent hover:border-line hover:bg-gray-100',
    FOCUS_RING,
  )
  const body = (
    <>
      <span className={cn('row-span-2 grid size-9 place-items-center rounded-md', iconClass)}>
        <Icon name={icon} className="size-4.5" />
      </span>
      <span className="truncate font-semibold text-gray-900">{name}</span>
      <span className="flex min-w-0 items-center gap-1 text-xs text-gray-500">
        {external && <Icon name="external" className="size-3 shrink-0" />}
        <span className="truncate">{sub}</span>
      </span>
    </>
  )

  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {body}
      </a>
    )
  }
  return (
    <Link to={to!} aria-current={current ? 'true' : undefined} className={className}>
      {body}
    </Link>
  )
}
